function floorToDecimals(value: number, decimals: number): number {
	if (decimals <= 0) {
		return Math.floor(value);
	}
	const pow = 10 ** decimals;
	return Math.floor(value * pow) / pow;
}

function decimalsForScaledValue(scaled: number): 0 | 1 | 2 {
	if (scaled >= 100) {
		return 0;
	}
	if (scaled >= 10) {
		return 1;
	}
	return 2;
}

/**
 * Formats a number into a short, readable label:
 * - `< 10,000`: adds thousands separators (e.g. `1,014`)
 * - `>= 10,000`: uses `K`, `M`, `B` suffixes, with 0–2 decimals depending on magnitude
 *
 * Examples:
 * - `999` → `999`
 * - `1014` → `1,014`
 * - `10150` → `10.1K`
 * - `555200` → `555K`
 * - `1091091` → `1.09M`
 * - `10590000` → `10.5M`
 * - `256999999` → `256M`
 * - `2147483647` → `2.14B`
 */
export function formatShorthandNumber(input: number): string {
	if (!Number.isFinite(input)) {
		return "0";
	}

	const sign = input < 0 ? "-" : "";
	const n = Math.abs(input);

	if (n < 10_000) {
		return (
			sign +
			new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
				Math.floor(n),
			)
		);
	}

	const scales = [
		{ suffix: "B", value: 1_000_000_000 },
		{ suffix: "M", value: 1_000_000 },
		{ suffix: "K", value: 1_000 },
	] as const;

	const scale = scales.find((s) => n >= s.value) ?? scales[scales.length - 1];
	const rawScaled = n / scale.value;
	const decimals = decimalsForScaledValue(rawScaled);
	const scaled = floorToDecimals(rawScaled, decimals);

	const formatted =
		decimals === 0
			? scaled.toFixed(0)
			: decimals === 1
				? scaled.toFixed(1)
				: scaled.toFixed(2);

	return `${sign}${formatted}${scale.suffix}`;
}

/**
 * Always full integer with thousands separators (never K/M/B).
 * e.g. `1014` → `1,014`, `2147483647` → `2,147,483,647`
 */
export function formatCommaNumber(input: number): string {
	if (!Number.isFinite(input)) {
		return "0";
	}
	const sign = input < 0 ? "-" : "";
	const n = Math.abs(input);
	return (
		sign +
		new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
			Math.floor(n),
		)
	);
}

